import { isEmpty } from "@swiggy-private/js-utils";
import { AnalyticsEventPayload } from "@swiggy-private/analytics/dist/interfaces";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { IPopUpWidgetProps } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/PopUpWidget/types";
import { CATEGORY_HEADING_ID, MENU_CONTAINER_ID, SCROLL_OFFSET } from "./constants";
import { injectRenderCrossSellIntoCard } from "./injectCrossSell";
import { ItemCategoryCardShape, WidgetTypes } from "./types";
import type { FilterMenuDataParams } from "./types";
interface FilteredMenuData {
    filteredData: IPLWidgetControllerCards[];
    fabWidgetData: IPLWidgetControllerCards[];
}

/**
 * Converts popup config card to PLWidgetController card format
 * @param popupCard - The popup card from configs.POPUP.configInfo[0].card
 * @returns IPLWidgetControllerCards formatted card or undefined
 */
export const createPopupCard = (
    popupCard: IPopUpWidgetProps
): IPLWidgetControllerCards | undefined => {
    if (!popupCard || !popupCard["@type"]) {
        return;
    }

    return {
        card: {
            card: popupCard,
        },
    } as IPLWidgetControllerCards;
};

// Helper to find the scrollable menu container by ID
const findScrollableContainer = () => {
    return document?.getElementById(MENU_CONTAINER_ID);
};

export const fallbackScroll = (percentage: number) => {
    const fallbackTop = window.innerHeight * percentage;
    findScrollableContainer()?.scrollTo({ top: fallbackTop, left: 0, behavior: "smooth" });
};

/**
 * Scrolls to the top of the menu, preferring to align to the first category heading if present.
 * Falls back to scrolling the container or window based on availability.
 * @param percentage - Fallback scroll position as a percentage of the viewport height
 */
export const scrollToTop = (percentage?: number) => {
    const scrollContainer = findScrollableContainer();
    // Check if the container can actually scroll (content overflows)
    const canScrollContainer = !!(
        scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight + 1
    );

    // Prefer heading-based scroll if available
    const heading = document?.getElementById(CATEGORY_HEADING_ID) as HTMLElement | null;

    if (heading) {
        // If we have a scrollable container, scroll it to the heading
        if (canScrollContainer && scrollContainer) {
            const headingRect = heading.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const desired =
                scrollContainer.scrollTop + (headingRect.top - containerRect.top) - SCROLL_OFFSET;
            const maxTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const targetTop = Math.max(0, Math.min(desired, maxTop));
            scrollContainer.scrollTo({ top: targetTop, left: 0, behavior: "smooth" });
            return;
        }
        // Otherwise, scroll the window to the heading
        const headingRect = heading.getBoundingClientRect();
        const desired = window.scrollY + headingRect.top - SCROLL_OFFSET;
        const doc = document.documentElement;
        const body = document.body;
        const maxScrollTop =
            Math.max(doc?.scrollHeight || 0, body?.scrollHeight || 0) - window.innerHeight;
        const targetTop = Math.max(0, Math.min(desired, maxScrollTop));
        window.scrollTo({ top: targetTop, left: 0, behavior: "smooth" });
        return;
    }

    // Fallback to container-top based scroll if no heading is found
    if (scrollContainer) {
        if (canScrollContainer) {
            scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            return;
        }
        // If container can't scroll, scroll the window to the container's top
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetTop = Math.max(0, window.scrollY + containerRect.top - SCROLL_OFFSET);
        window.scrollTo({ top: targetTop, left: 0, behavior: "smooth" });
        return;
    }

    // Final fallback: scroll window to a percentage of the viewport height
    if (percentage) {
        fallbackScroll(percentage);
    }
};

/**
 * Counts the number of item categories in the given cards array that match the specified parentType.
 * @param cards - Array of IPLWidgetControllerCards
 * @param parentType - The parent type to match against
 * @returns Number of matching item categories
 */
export const getCategoryListCount = (
    cards: IPLWidgetControllerCards[],
    parentType: string
): number => {
    // Return 0 if parentType is empty
    if (isEmpty(parentType)) return 0;

    // Reduce over cards to count matching categories
    return cards.reduce((count, card) => {
        const inner = card?.card?.card;
        if (
            !inner ||
            typeof inner !== "object" ||
            !("@type" in inner) ||
            !("parentType" in inner)
        ) {
            return count;
        }

        const cardType = inner["@type"] as string;
        const cardParentType = (inner as { parentType: string }).parentType;

        // Count if the card is an item category and matches the parentType
        if (
            (cardType === WidgetTypes.ITEM_CATEGORY_TYPE ||
                cardType === WidgetTypes.NESTED_ITEM_CATEGORY_TYPE) &&
            cardParentType === parentType
        ) {
            return count + 1;
        }

        return count;
    }, 0);
};

interface CategoryCardExtensions extends ItemCategoryCardShape {
    "@type": string;
    enableNudgeForMinAddons?: boolean;
    itemExpandImpression?: (analyticsObj?: AnalyticsEventPayload) => void;
}

export const hasType = (
    card: IPLWidgetControllerCards
): card is IPLWidgetControllerCards & { card: { card: CategoryCardExtensions } } => {
    return (
        card?.card?.card && typeof (card.card.card as { "@type"?: string })["@type"] === "string"
    );
};

export const filterMenuData = ({
    filteredData,
    initialData,
    itemExpandImpression,
    getCrossSellRenderer,
}: FilterMenuDataParams): FilteredMenuData => {
    let filteredCards = filteredData.filter(
        (card) => !hasType(card) || card.card.card["@type"] !== WidgetTypes.FAB_WIDGET
    );

    filteredCards = filteredCards.map((card) => {
        if (
            hasType(card) &&
            (card.card.card["@type"] === WidgetTypes.ITEM_CATEGORY_TYPE ||
                card.card.card["@type"] === WidgetTypes.NESTED_ITEM_CATEGORY_TYPE)
        ) {
            const cardObj = card.card.card;
            cardObj.itemExpandImpression = (analyticsObj?: AnalyticsEventPayload) => {
                if (analyticsObj) {
                    itemExpandImpression(analyticsObj);
                }
            };
            cardObj.enableNudgeForMinAddons = true;
            // Inject cross sell into card
            const isolatedCardObj = { ...cardObj };
            injectRenderCrossSellIntoCard(isolatedCardObj, getCrossSellRenderer);
            return {
                ...card,
                card: { ...card.card, card: isolatedCardObj },
            } as IPLWidgetControllerCards;
        }
        return card;
    });

    const fabWidgetCards = initialData.filter(
        (card) => hasType(card) && card.card.card["@type"] === WidgetTypes.FAB_WIDGET
    );

    return {
        filteredData: filteredCards,
        fabWidgetData: fabWidgetCards,
    };
};
